/**
 * Manager Assistant security settings (Pulse page): set / change / remove the
 * 4-digit quick-access PIN, and sign out. Mirrors the client app's settings.
 */
import { useState } from 'react';
import { Lock, LogOut, Check, Loader2, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useManager, getHasPinCached, setPin as apiSetPin, deletePin as apiDeletePin, logout as managerLogout,
} from '@/services/api/manager-auth';

export default function ManagerSecuritySettings() {
  const manager = useManager();
  const [hasPin, setHasPin] = useState(getHasPinCached());
  const [editing, setEditing] = useState(false);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const save = async () => {
    if (!/^\d{4}$/.test(pin)) { setError('Enter a 4-digit PIN'); return; }
    setBusy(true); setError('');
    try {
      await apiSetPin(pin);
      setHasPin(true); setEditing(false); setPin(''); setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { setError('Could not save PIN'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true); setError('');
    try { await apiDeletePin(); setHasPin(false); }
    catch { setError('Could not remove PIN'); }
    finally { setBusy(false); }
  };

  const signOut = () => { managerLogout(); window.location.href = '/pa/login'; };

  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-[var(--v4-accent)]" />
        <h3 className="text-sm font-semibold text-foreground">Security</h3>
      </div>

      {manager?.phone && (
        <p className="text-xs text-muted-foreground">Signed in as {manager.phone}</p>
      )}

      {/* PIN */}
      <div className="rounded-lg bg-muted/50 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-foreground">Quick-access PIN</span>
            {hasPin && <span className="text-[10px] font-medium text-green-600 dark:text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded-full">On</span>}
            {saved && <Check className="h-4 w-4 text-green-600 dark:text-green-400" />}
          </div>
          {!editing && (
            <button onClick={() => { setEditing(true); setPin(''); setError(''); }}
              className="text-xs font-medium text-[var(--v4-accent)] hover:underline">
              {hasPin ? 'Change' : 'Set PIN'}
            </button>
          )}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">A 4-digit PIN lets you re-open the app without an SMS code.</p>

        {editing && (
          <div className="mt-3 flex items-center gap-2">
            <input
              type="password" inputMode="numeric" maxLength={4} autoFocus
              value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="••••"
              className="w-24 rounded-lg border border-border bg-card px-3 py-2 text-center text-lg tracking-[0.4em] text-foreground outline-none focus:border-[var(--v4-accent)]"
            />
            <button onClick={save} disabled={busy}
              className={cn('flex items-center gap-1 rounded-lg bg-[var(--v4-accent)] px-3 py-2 text-sm font-medium text-white', busy && 'opacity-60')}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </button>
            <button onClick={() => { setEditing(false); setPin(''); setError(''); }}
              className="rounded-lg bg-muted px-3 py-2 text-sm text-foreground">Cancel</button>
          </div>
        )}
        {hasPin && !editing && (
          <button onClick={remove} disabled={busy} className="mt-2 text-xs text-red-600 dark:text-red-400 hover:underline">
            Remove PIN
          </button>
        )}
        {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
      </div>

      <button onClick={signOut}
        className="flex w-full items-center justify-center gap-2 rounded-lg border border-border px-3 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-500/10 dark:text-red-400">
        <LogOut className="h-4 w-4" /> Sign out
      </button>
    </div>
  );
}
